/**
 * Resolves the backend origin for browser-side API calls.
 *
 * This is a decoupled SPA with no Vite proxy: the browser talks to the Django
 * backend at an absolute origin. In production a reverse proxy (Caddy) serves
 * frontend and backend same-origin, so we just reuse the current origin. In dev
 * they are two processes on two ports, so we need to point at the backend's port.
 *
 * To run several projects at once without port collisions, set the dev ports in
 * `frontend/.env`:
 *   VITE_FRONTEND_PORT=3001            # what port the Vite dev server binds to
 *   VITE_DEV_BACKEND_ORIGIN=http://127.0.0.1:8001   # where the backend lives
 *
 * `VITE_DEV_BACKEND_ORIGIN`, when set, wins. Otherwise we derive the backend
 * port from the current frontend port via a small fallback map, so the var stays
 * optional for the default 3000/8000 setup. Only applied for local hosts — on a
 * real hostname we always use the same origin (Caddy).
 *
 * See frontend/docs/dev-env-vars.md for the full rationale.
 */

// Fallback frontend-port -> backend-port map, used only when
// VITE_DEV_BACKEND_ORIGIN is not set. Keeps the common local setups working
// with zero config.
const DEV_PORT_MAP = {
    3000: '8000',
    3001: '8001',
    3002: '8002'
}

// Any loopback host counts as local: `localhost` plus the whole 127.0.0.0/8
// range. Per-project host-port isolation binds each project to its own loopback
// IP (e.g. 127.0.0.3), and the browser then runs at that address — so we must
// treat 127.0.0.x, not just 127.0.0.1, as local. See docs/multi-project-ports.md.
//
// `*.localhost` counts too (RFC 6761 reserves it for loopback), which is what lets a partner
// host be simulated in dev: `acme.power.localhost:3000` resolves the `acme` partner's branding
// while still talking to the dev backend. See docs/dev-env-vars.md.
export const isLocalHost = (hostname) =>
    hostname === 'localhost' ||
    hostname.endsWith('.localhost') ||
    /^127\.\d+\.\d+\.\d+$/.test(hostname)

/**
 * @returns {string} Backend origin, e.g. `http://127.0.0.1:8001` in dev or
 *   `https://apps.fettes.com` in production. No trailing slash.
 */
export function apiOrigin() {
    const { protocol, hostname, port } = window.location

    if (isLocalHost(hostname)) {
        const configured = import.meta.env.VITE_DEV_BACKEND_ORIGIN?.trim()
        if (configured) {
            return configured.replace(/\/+$/, '')
        }
        const backendPort = DEV_PORT_MAP[port] || port
        const portPart = backendPort ? `:${backendPort}` : ''
        return `${protocol}//${hostname}${portPart}`
    }

    // Production / any non-local host: same-origin (served together by Caddy).
    const portPart = port ? `:${port}` : ''
    return `${protocol}//${hostname}${portPart}`
}
